/* eslint-disable max-params, no-param-reassign -- these linting errors come up constantly with reducers */
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import type { RootState } from "@/store";
import { useAppDispatch } from "@/store/hooks";
import { extraReducersFactory } from "@/store/reducers/reset";

interface SMTPhotoUploadState {
  // Map of entryId -> local photo URI for entries being uploaded or recently uploaded
  entryPhotos: Record<string, string>;
  // Currently uploading entry ID (for showing spinner overlay)
  uploadingEntryId: null | string;
}

const initialState: SMTPhotoUploadState = {
  entryPhotos: {},
  uploadingEntryId: null,
};

const smtPhotoUploadSlice = createSlice({
  // NOTE: extraReducers is required for resetAllReduxSlices to work as expected
  extraReducers: extraReducersFactory(initialState),
  initialState,
  name: "smtPhotoUpload",
  reducers: {
    clearUpload: (state) => {
      // Only clear the uploading flag, keep the photos
      state.uploadingEntryId = null;
    },
    startUpload: (
      state,
      action: PayloadAction<{ entryId: string; photo: FilePickerAsset }>,
    ) => {
      state.uploadingEntryId = action.payload.entryId;
      state.entryPhotos[action.payload.entryId] = action.payload.photo.uri;
    },
  },
});

const { clearUpload: clearUploadAction, startUpload: startUploadAction } =
  smtPhotoUploadSlice.actions;

const selectUploadingEntryId = (state: RootState) =>
  state.smtPhotoUpload.uploadingEntryId;
const selectEntryPhotoUri = (entryId: string) => (state: RootState) =>
  state.smtPhotoUpload.entryPhotos[entryId];

const useSMTPhotoUploadDispatch = () => {
  const dispatch = useAppDispatch();

  const startUpload = (entryId: string, photo: FilePickerAsset) => {
    dispatch(startUploadAction({ entryId, photo }));
  };

  const clearUpload = () => {
    dispatch(clearUploadAction());
  };

  return {
    clearUpload,
    startUpload,
  };
};

export type { SMTPhotoUploadState };

export {
  clearUploadAction,
  selectEntryPhotoUri,
  selectUploadingEntryId,
  startUploadAction,
  useSMTPhotoUploadDispatch,
};
export default smtPhotoUploadSlice.reducer;
